/** `traverse.slide` — one-way chutes. A slide is any entity carrying the
 * configured tag; its box is the chute's extent and its facing the way it
 * descends (facing 1: from the top-left corner down to the bottom-right).
 * Pressing the entry direction while standing at the top end HOLDS the body
 * (the `heldBy` protocol) and carries it along the chute at a fixed speed —
 * no steering — then releases it at the bottom end to land under the
 * platformer. Publishes each chute as a one-way solver navigation link. */
import { id, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read, str } from '../module.js';
import { heldBy } from '../physics.js';
import { deltaX, type EntityV1 } from '../world.js';
import { ANIM_STATE_NAMES, type AnimStateName } from '../../animStates.js';

export type SlideConfig = {
  slideTag: string;
  /** Ride speed along the chute (units/s). */
  speed: number;
  /** Horizontal distance from the top end within which the chute is entered. */
  reach: number;
  enter: 'up' | 'down';
  /** Pose while riding. */
  state: AnimStateName;
};

/** The chute's end points (centre/bottom box convention). */
export function chuteEnds(s: EntityV1): { top: { x: number; y: number }; bottom: { x: number; y: number } } {
  return { top: { x: s.x - s.facing * s.w / 2, y: s.y - s.h }, bottom: { x: s.x + s.facing * s.w / 2, y: s.y } };
}

export const slide = defineModule<SlideConfig>({
  id: 'traverse.slide', version: 1, attach: 'entity', role: 'behaviour', surfaceContact: true,
  summary: 'One-way chutes: enter at the top end and ride down at a fixed speed without steering, released at the bottom end.',
  config: obj({ slideTag: id(), speed: num(0.5, 30), reach: num(0.05, 2), enter: oneOf('up', 'down'), state: oneOf(...ANIM_STATE_NAMES) }),
  requires: ['body.platformer'], events: ['slide', 'slide-end'],
  presentation: { slide: { cue: 'whoosh' }, 'slide-end': { cue: 'land' } },
  proofs: ['enters-at-top-only', 'rides-at-fixed-speed', 'no-steering', 'one-way', 'released-at-bottom-lands', 'ride-state'],
  animStates: c => [c.state],
  init(ctx, e) { Object.assign(ctx.data(e), { hold: false, slide: null, t: 0 }); },
  systems: {
    think(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e), world = ctx.world;
      if (e.status !== 'active') { d.hold = false; d.slide = null; return; }
      if (heldBy(e, 'traverse.slide')) return;
      let chute = d.hold === true ? ctx.entity(str(d, 'slide')) : undefined;
      if (!chute) {
        d.hold = false; d.slide = null;
        const input = ctx.input(e.slot ?? 0);
        if (!e.grounded || input.dy !== (c.enter === 'up' ? -1 : 1)) return;
        chute = ctx.entities({ tag: c.slideTag }).find(s => {
          const { top } = chuteEnds(s);
          return Math.abs(deltaX(world, top.x, e.x)) <= c.reach && Math.abs(e.y - top.y) <= 0.06;
        });
        if (!chute) return;
        Object.assign(d, { hold: true, slide: chute.id, t: 0 });
        e.grounded = false; e.groundId = null;
        ctx.emit('slide', { x: e.x, y: e.y, slot: e.slot ?? 0 });
      }
      const { top, bottom } = chuteEnds(chute);
      const length = Math.hypot(bottom.x - top.x, bottom.y - top.y);
      const t = Math.min(1, read(d, 't') + (c.speed * ctx.dt) / length);
      d.t = t;
      e.x = top.x + (bottom.x - top.x) * t; e.y = top.y + (bottom.y - top.y) * t;
      e.vx = ((bottom.x - top.x) / length) * c.speed; e.vy = ((bottom.y - top.y) / length) * c.speed;
      e.facing = chute.facing;
      if (t >= 1) {
        Object.assign(d, { hold: false, slide: null, t: 0 });
        e.vx = 0; e.vy = 0;
        ctx.emit('slide-end', { x: e.x, y: e.y, slot: e.slot ?? 0 });
      }
    },
    present(ctx, e) {
      if (e && e.status === 'active' && ctx.data(e).hold === true) ctx.setAnim(e, ctx.config.state);
    },
  },
  navLinks(ctx, e, player) {
    if (!e || e.id !== player.id) return [];
    return ctx.entities({ tag: ctx.config.slideTag }).map(s => {
      const { top, bottom } = chuteEnds(s);
      return { from: top, to: bottom, cost: Math.hypot(bottom.x - top.x, bottom.y - top.y) * 0.5, dy: ctx.config.enter === 'up' ? -1 as const : 1 as const, along: true, label: 'slide' };
    });
  },
});
